import {shallowEqual, useDispatch, useSelector} from "react-redux";

import {selectByLanguage} from "@reva/shared/i18n";

import {useRevaTranslation} from "../../hooks/languageHooks";
import {
    selectActiveValueForSingleParameterCurried,
    selectSingleParamErrorCurried,
} from "../../redux/objectParametersSelectors";
import {selectEditedValueParamErrors, selectSegment} from "../../redux/objectParametersSlice";
import {
    updateMultipleParamsAndValidateAndSubmitFormThunk,
    updateParamAndValidateAndSubmitFormThunk,
} from "../../redux/objectParametersThunk";
import {selectApiInputParamForCurrentSegmentCurried} from "../../redux/specSelectors";

/**
 * @typedef SpecCategoryDef Category from Spec
 * @property {number} categoryNumber categoryNumber
 * @property {string} shortNameDe Category name (DE)
 * @property {string} shortNameEn Category name (EN)
 * @property {string|null} [titleDe] Category title (DE)
 * @property {string|null} [titleEn] Category title (EN)
 * @property {string} [groupDe] Group the category is shown in (DE)
 * @property {string} [groupEn] Group the category is shown in (EN)
 */

/**
 * @typedef InputParameterDetailsDef Parameter Details properties
 * @property {string} [parameter] parameter name (optional)
 * @property {VALUE_PARAMETER_DATA_TYPE} type datatype of the parameter
 * @property {string|string[]|null} [onlyInSegment] optional flag, if the param should only be valid for specific flags
 * @property {boolean|null} [required] is the parameter required?
 * @property {string|null} [nameDe] Name (DE)
 * @property {string|null} [nameEn] Name (En)
 * @property {string|null} [unitDe] Unit (DE)
 * @property {string|null} [unitEn] Unit (EN)
 * @property {number|null} [minValue] optional min value of number input
 * @property {number|null} [maxValue] optional max value of number input
 * @property {SpecCategoryDef[]|null} [categories]
 */

/**
 * @typedef {object} SpecFormattingDef Formatting options from Spec
 * @property {number|null} [minDecimalPlaces] minimal decimal places
 * @property {number|null} [maxDecimalPlaces] maximal decimal places
 */

/**
 * @typedef SpecInputParamDef Input Parameter from Spec
 * @property {string} parameter parameter name
 * @property {VALUE_PARAMETER_DATA_TYPE} type datatype of the parameter
 * @property {boolean} required is the parameter required?
 * @property {string} nameDe Name (DE)
 * @property {string} nameEn Name (En)
 * @property {string|null} descriptionDe Name (DE)
 * @property {string|null} descriptionEn Name (En)
 * @property {string|null} [guiAdditionalHelpTextDe] optional additional GUI help text (DE), may contain HTML
 * @property {string|null} [guiAdditionalHelpTextEn] optional additional GUI help text (EN), may contain HTML
 * @property {string|null} [unitDe] Unit (DE)
 * @property {string|null} [unitEn] Unit (EN)
 * @property {number|string|null} [minValue] optional min value (number, or ISO date string for type "date")
 * @property {number|string|null} [maxValue] optional max value (number, or ISO date string for type "date")
 * @property {SpecFormattingDef|null} [formatting] optional formatting options
 * @property {SpecCategoryDef[]|null} [categories]
 */

/**
 * @typedef InputParamDef
 * @property {string} parameter parameter name
 * @property {VALUE_PARAMETER_DATA_TYPE} type the datatype of the parameter
 * @property {boolean} required is the parameter required?
 * @property {string} nameDe Name (DE)
 * @property {string} nameEn Name (En)
 * @property {string|null} descriptionDe Name (DE)
 * @property {string|null} descriptionEn Name (En)
 * @property {string|null} [guiAdditionalHelpTextDe] optional additional GUI help text (DE), may contain HTML
 * @property {string|null} [guiAdditionalHelpTextEn] optional additional GUI help text (EN), may contain HTML
 * @property {string|null} [unitDe] Unit (DE)
 * @property {string|null} [unitEn] Unit (EN)
 * @property {number|string|null} [minValue] optional min value (number, or ISO date string for type "date")
 * @property {number|string|null} [maxValue] optional max value (number, or ISO date string for type "date")
 * @property {string|null} [displayFormat] optional formatting of the value
 * @property {SpecFormattingDef|null} [formatting] optional formatting options
 * @property {SpecCategoryDef[]|null} [categories]
 */

/**
 * @callback ParamValueChangeHandler
 * @param {*} value updated value
 * @param {string|null} [comment] optional updated comment
 * @return {Promise<void>|undefined} resolves once the value has persisted (awaitable to sequence a dependent write)
 */

/**
 * React Hook to gain access to a specific parameter value.
 * @param {string} parameter parameter name
 * @param {RevaValueParameterType} parameterType parameter type
 * @return {{parameterValues: ActiveParameterValue, onValueChange: ParamValueChangeHandler}} parameter values and a function to update the value
 */
export const useParameterValue = (parameter, parameterType) => {
    const parameterValues = useSelector(selectActiveValueForSingleParameterCurried(parameter, parameterType));
    const dispatch = useDispatch();

    const onValueChange = (value, comment) => {
        return dispatch(updateParamAndValidateAndSubmitFormThunk(parameter, parameterType, value, comment));
    };
    return {parameterValues, onValueChange};
};

/**
 * React Hook to read and update multiple parameters of the same type in one call.
 * Persists all changes in a single API request via persistMultipleParameters.
 * @param {string[]} parameters list of parameter names — must be a stable reference (e.g. module-level const)
 * @param {string} parameterType e.g. "INSPECTION_DERIVED"
 * @return {{parameterValues: Record<string, any>, onValuesChange: (params: Record<string, any>, comments?: Record<string, string | null>) => void}}
 */
export const useMultipleParameterValues = (parameters, parameterType) => {
    const dispatch = useDispatch();
    const parameterValues = useSelector(
        (state) =>
            Object.fromEntries(
                parameters.map((p) => [p, selectActiveValueForSingleParameterCurried(p, parameterType)(state)]),
            ),
        shallowEqual,
    );

    const onValuesChange = (params, comments) => {
        dispatch(updateMultipleParamsAndValidateAndSubmitFormThunk(parameterType, params, comments));
    };

    return {parameterValues, onValuesChange};
};

/**
 * React Hook to use an input parameter spec, either from
 * @param {string} parameter parameter or parameter details name to query
 * @param {InputParameterDetailsDef|null} [parameter_details_def] optional override for the parameter spec, to use a parameter details instead
 * @return {InputParamDef|null} resolved input parameter spec (or null, if parameter is not valid)
 */
export const useInputParameterDef = (parameter, parameter_details_def) => {
    const currentSegment = useSelector(selectSegment);
    /** @type {SpecInputParamDef|null} */
    const specInputParamDef = useSelector(selectApiInputParamForCurrentSegmentCurried(parameter));

    if (parameter_details_def) {
        const {
            parameterType,
            type,
            onlyInSegment,
            required,
            nameDe,
            nameEn,
            unitDe,
            unitEn,
            minValue,
            maxValue,
            categories,
        } = parameter_details_def;
        if ((typeof onlyInSegment === "string" && onlyInSegment !== currentSegment) || !useInputParameterDef) {
            return null;
        }
        if (Array.isArray(onlyInSegment) && onlyInSegment.includes(currentSegment)) {
            return null;
        }
        return {
            parameter,
            parameterType,
            type,
            required: !!required,
            nameDe: nameDe || parameter,
            nameEn: nameEn || parameter,
            descriptionDe: null,
            descriptionEn: null,
            unitDe,
            unitEn,
            minValue,
            maxValue,
            categories: categories || null,
        };
    } else if (specInputParamDef) {
        const {
            type,
            required,
            nameDe,
            nameEn,
            descriptionDe,
            descriptionEn,
            guiAdditionalHelpTextDe,
            guiAdditionalHelpTextEn,
            unitDe,
            unitEn,
            minValue,
            maxValue,
            formatting,
            categories,
        } = specInputParamDef;
        return {
            parameter,
            type,
            required,
            nameDe,
            nameEn,
            descriptionDe,
            descriptionEn,
            guiAdditionalHelpTextDe,
            guiAdditionalHelpTextEn,
            unitDe,
            unitEn,
            minValue,
            maxValue,
            formatting,
            categories,
        };
    }
    return null;
};

/**
 * Returns a localized validation message for a parameter.
 * @param {string} param parameter name
 * @param {RevaValueParameterType} parameterType parameter type e.g.
 * @param {boolean} hasConflict flag, if the parameter has a conflict
 * @return {string|undefined} localized validation message
 * */
export const useLocalizedFieldValidationMessage = (param, parameterType, hasConflict) => {
    const {t, language} = useRevaTranslation();
    let requiredFieldsValidation = useSelector(selectSingleParamErrorCurried(param));
    const tempErrors = useSelector(selectEditedValueParamErrors);
    // #17888 Zeige keine Fehler auf anderen Persistenzschichten als AVM an
    if (parameterType !== "AVM") return undefined;
    if (requiredFieldsValidation?.length === 0 && tempErrors != null) {
        requiredFieldsValidation = tempErrors;
    }

    if (Array.isArray(requiredFieldsValidation) && requiredFieldsValidation.length > 0) {
        /**
         * @type {ObjectParamError}
         */
        const errorObject = requiredFieldsValidation[0];

        if (errorObject.languageKey) {
            return t(errorObject.languageKey, errorObject.languageParams);
        }

        if (errorObject.error_de && errorObject.error_en) {
            return selectByLanguage(language, errorObject.error_de, errorObject.error_en);
        }
    }

    if (hasConflict) {
        return t("object.error.conflict");
    }

    return undefined;
};

/**
 * Returns a border class name for the given ResolvedParameterSource and conflict state.
 * @param {ResolvedParameterSource|null} type
 * @param {boolean} [hasConflict]
 * @param {boolean} [softColor]
 * @return {string} border class name
 */
export function getResourceColor(type, hasConflict = false, softColor = false) {
    if (hasConflict) {
        return "red-500";
    }

    switch (type) {
        case "initialValuation":
            return softColor ? "yellow-300" : "yellow-500";
        case "inspection":
            return softColor ? "cyan-300" : "cyan-400";
        case "inspectionDerived":
            return softColor ? "cyan-300" : "cyan-400";
        case "plot":
            return softColor ? "pink-300" : "pink-400";
        case "register":
            return softColor ? "orange-300" : "orange-400";
        case "energy":
            return softColor ? "purple-500" : "purple-600";
        case "idpEnergyCertificate":
            return softColor ? "purple-200" : "purple-300";
        case "immodrop":
            // needs a dark-theme-aware shade, unlike the fixed colors above — see --immodrop-color (layout.scss)
            return softColor ? "gray-400" : "[var(--immodrop-color)]";
        case "reva":
            return softColor ? "blue-400" : "blue-500";
        case "firstFinalize":
            return softColor ? "green-300" : "green-600";
        case "fallback":
            return "gray-300";
        default:
            return "";
    }
}
